import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Message} from "primereact/message";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import type {ValuationData} from "@reva/shared/valuation-data";

import type {ArchiveDocumentType} from "../../api/payload-types/persistence-finalize";
import OverriddenVariantAppreciationsWarning from "../../component/appreciation/OverriddenVariantAppreciationsWarning";
import I18nErrorMessage from "../../component/I18nErrorMessage";
import {selectUsername} from "../../redux/authSlice";
import {
    removeFinalizationError,
    selectFinalizationError,
    selectFinalizationLoading,
    selectLastValidPersistenceResponse,
    selectPendingWrites,
    selectRevaId,
    setFinalizationLoading,
} from "../../redux/persistenceSlice";
import {fetchPersistenceByIdInternal, finalizePersistence} from "../../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import type {ApiQueryError} from "../../redux/types";
import {buildApiQueryError, generateDefaultQueryError} from "../../util/errorUtil";
import {checkForPersistenceChanges} from "../../util/persistenceChangeUtil";
import ArchiveDocumentsSelection, {
    defaultDocTypeSelection,
    useAllowedArchiveDocumentTypes,
} from "./ArchiveDocumentsSelection";
import {useArchivedDocumentsHandler} from "./DialogArchivedDocuments";
import DialogFinalizeValuationChanged from "./DialogFinalizeValuationChanged";

interface Props {
    /** Ob der Dialog sichtbar ist */
    visible: boolean;
    /** Setter für die Sichtbarkeit des Dialogs */
    setVisible: (visible: boolean) => void;
    /** Callback nach erfolgreichem Finalisieren, das dem aktuellen User die Bewertung entzogen hat. */
    onHandedOff?: () => void;
}

/**
 * Dialog to finalize a Valuation.
 */
export default function DialogFinalizeValuation({visible, setVisible, onHandedOff}: Props) {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();

    const revaId = useRevaSelector(selectRevaId);
    const currentUsername = useRevaSelector(selectUsername);
    const lastValidPersistenceResponse = useRevaSelector(selectLastValidPersistenceResponse);
    const finalizationLoading = useRevaSelector(selectFinalizationLoading);
    const finalizationError = useRevaSelector(selectFinalizationError);
    const pendingWrites = useRevaSelector(selectPendingWrites);

    const allowedDocTypes = useAllowedArchiveDocumentTypes();
    const {handleArchivedDocuments, archivedDocumentsDialog} = useArchivedDocumentsHandler();

    const [persistenceSnapshot, setPersistenceSnapshot] = useState<ValuationData | null>(null);
    const [persistenceFromApi, setPersistenceFromApi] = useState<ValuationData | null>(null);
    const [persistenceLoadError, setPersistenceLoadError] = useState<ApiQueryError | null>(null);
    const [hasPersistenceChanged, setHasPersistenceChanged] = useState(false);
    const [selectedDocTypes, setSelectedDocTypes] = useState<ArchiveDocumentType[]>([]);
    /** Merker, damit der Vergleichsstand pro Öffnen nur einmal genommen wird. */
    const snapshotTaken = useRef(false);

    // Beim Öffnen: Snapshot aufnehmen und frische Daten vom Backend laden.
    // Warten, bis keine eigenen Schreibvorgänge mehr laufen (pendingWrites).
    // Beim Schließen: lokalen State aufräumen.
    useEffect(() => {
        if (!visible) {
            snapshotTaken.current = false;
            setPersistenceSnapshot(null);
            setPersistenceFromApi(null);
            setPersistenceLoadError(null);
            setHasPersistenceChanged(false);
            setSelectedDocTypes([]);
            return;
        }
        // Erst vergleichen, wenn die eigenen Schreibvorgänge durch sind: solange noch ein Auto-Save
        // unterwegs ist, kennt lastValidPersistenceResponse die gerade eingegebene Änderung nicht, und
        // der Server meldet sie als fremde Änderung zurück ("Änderungen erkannt" auf den eigenen Wert).
        // Bis dahin bleibt persistenceFromApi null, der Dialog zeigt also seinen Lade-Spinner.
        if (pendingWrites > 0 || snapshotTaken.current) {
            return;
        }
        snapshotTaken.current = true;
        // Vorauswahl: keine Dokumente — außer es gibt genau einen erlaubten Typ.
        setSelectedDocTypes(defaultDocTypeSelection(allowedDocTypes));
        const snapshot = lastValidPersistenceResponse as ValuationData;
        setPersistenceSnapshot(snapshot);
        // Vergleich direkt im .then() ausführen, um einen Render-Zyklus ohne korrekten
        // hasPersistenceChanged-Wert zu vermeiden
        dispatch(fetchPersistenceByIdInternal(revaId!, true))
            .then((apiData) => {
                setPersistenceFromApi(apiData);
                setHasPersistenceChanged(checkForPersistenceChanges(apiData, snapshot));
            })
            .catch((err) => setPersistenceLoadError(buildApiQueryError(generateDefaultQueryError(err as Error))));
    }, [visible, pendingWrites]); // eslint-disable-line react-hooks/exhaustive-deps

    const onFinalizeDialogClose = () => {
        dispatch(removeFinalizationError());
        setVisible(false);
    };

    const onFinalize = async () => {
        dispatch(removeFinalizationError());
        dispatch(setFinalizationLoading(true));
        // Owner unmittelbar VOR dem Finalisieren (das Backend nullt ihn dabei) — für den
        // "dir nicht mehr zugeordnet"-Toast, der nur erscheinen soll, wenn es der eigene war.
        let ownerBefore: string | null = null;
        await dispatch(fetchPersistenceByIdInternal(revaId!, true))
            .then((persistence) => {
                setPersistenceFromApi(persistence);
                ownerBefore = persistence?.metadata?.owner ?? null;
                const changed = checkForPersistenceChanges(persistence, persistenceSnapshot!);
                setHasPersistenceChanged(changed);
                return changed;
            })
            .then(async (hasPersistenceChanged: boolean) => {
                if (!hasPersistenceChanged) {
                    // Erfolg = Thunk liefert die Response (nicht null) — NICHT an finalDocument
                    // festmachen, das ist bei leerer Dokumentauswahl null.
                    const response = await dispatch(finalizePersistence(selectedDocTypes));
                    if (response) {
                        // Erfolg: Dialog schließen. Wurde genau EIN Dokument erzeugt, wird es direkt
                        // geöffnet; bei mehreren erscheint der Download-Dialog (s. Hook). Unabhängig
                        // davon sind die Dokumente jederzeit über das Snapshot-Untermenü des
                        // Lifecycle-Buttons abrufbar.
                        handleArchivedDocuments(response);
                        onFinalizeDialogClose();
                        if (ownerBefore === currentUsername) {
                            onHandedOff?.();
                        }
                    }
                } else {
                    dispatch(setFinalizationLoading(false));
                }
            })

            .catch((err: unknown) => {
                dispatch(setFinalizationLoading(false));
                setPersistenceLoadError(buildApiQueryError(generateDefaultQueryError(err as Error)));
            });
    };

    // Kein Finalize-Dialog anzeigen wenn nicht sichtbar — aber der Download-Dialog
    // der soeben erzeugten Dokumente öffnet sich genau NACH dem Schließen (ist null,
    // solange keine Dokumente anzubieten sind).
    if (!visible) return archivedDocumentsDialog;

    // Busy-Indikator während des initialen Ladens
    if (!persistenceFromApi && !persistenceLoadError) {
        return (
            <Dialog
                id="finalizeDialog"
                header={t("finalizeDialog.title")}
                style={{minWidth: "50rem"}}
                visible={true}
                onHide={() => {}}
                closable={false}
            >
                <div className="flex justify-center">
                    <div className="pi pi-spin pi-spinner text-6xl" />
                </div>
            </Dialog>
        );
    }

    // Fehler beim initialen Laden: schließbarer Dialog mit Fehlermeldung
    if (persistenceLoadError) {
        return (
            <Dialog
                id="finalizeDialog"
                header={t("finalizeDialog.title")}
                style={{minWidth: "50rem"}}
                visible={true}
                onHide={onFinalizeDialogClose}
            >
                <I18nErrorMessage errorObject={persistenceLoadError} className="block" />
            </Dialog>
        );
    }

    if (hasPersistenceChanged) {
        return (
            <DialogFinalizeValuationChanged
                persistenceFromApi={persistenceFromApi!}
                persistenceSnapshot={persistenceSnapshot!}
                onClose={() => setVisible(false)}
            />
        );
    }

    const footerContent = (
        <>
            <Button
                id="finalizeCancelButton"
                label={t("finalizeDialog.cancel")}
                icon={PrimeIcons.TIMES}
                severity="danger"
                className="mr-2"
                text
                onClick={onFinalizeDialogClose}
                disabled={finalizationLoading}
            />
            <Button
                id="finalizeSubmitButton"
                label={t("finalizeDialog.submit")}
                icon={PrimeIcons.CHECK}
                onClick={() => void onFinalize()}
                loading={finalizationLoading}
                // Auch hier sperren, solange ein Schreibvorgang läuft: onFinalize vergleicht erneut
                // gegen lastValidPersistenceResponse und würde sonst dieselbe Falle aufmachen.
                disabled={finalizationLoading || pendingWrites > 0}
            />
        </>
    );

    return (
        <Dialog
            id="finalizeDialog"
            header={t("finalizeDialog.title")}
            style={{minWidth: "50rem", minHeight: "10rem"}}
            visible={visible}
            onHide={() => setVisible(false)}
            closable={false}
            footer={footerContent}
        >
            <div className="flex flex-col gap-4">
                <Message
                    severity="warn"
                    text={t("finalizeDialog.finalizeConfirm")}
                    className="block white-space-pre-line leading-normal"
                />
                <OverriddenVariantAppreciationsWarning />
                <ArchiveDocumentsSelection
                    options={allowedDocTypes}
                    selected={selectedDocTypes}
                    setSelected={setSelectedDocTypes}
                    disabled={finalizationLoading}
                    idPrefix="finalize"
                />
                <I18nErrorMessage errorObject={finalizationError} className="block" />
            </div>
        </Dialog>
    );
}
